
import { Component, Prop, Watch } from 'nuxt-property-decorator'
import MobileMenu from '~/components/AppHeader/MobileMenu.vue'
import { OnBeforeDestroy, OnMounted } from '~/core/vue.types'
import AppHeaderContact from '~/components/AppHeader/AppHeaderContact.vue'
import AppHeaderDropdownMenu from '~/components/AppHeader/AppHeaderDropdownMenu/AppHeaderDropdownMenu.vue'
import AppLangSwitcher from '~/components/snippets/AppLangSwitcher.vue'
import { DropdownMenuItem } from '~/components/AppHeader/AppHeaderDropdownMenu/types'
import { FeatureFlags } from '~/core/config/feature-flags'
import GoogleRatingLite from '~/components/widgets/GoogleRatingLite.vue'
import { SiteNavigationMixin } from '~/mixins/site-navigation.mixin'
import { getExternalIndexHref, getExternalCompareMobileHref, getExternalCompareHomeHref } from '~/utils/external-links.utils'
import type { Theme } from '~/types/theme'

@Component({
  components: {
    GoogleRatingLite,
    MobileMenu,
    AppLangSwitcher,
    AppHeaderDropdownMenu,
    AppHeaderContact,
  },
})
export default class AppHeader extends SiteNavigationMixin implements OnMounted, OnBeforeDestroy {
  @Prop({
    required: false,
    default: 'default',
  })
  readonly type!: 'default' | 'mobile-like' | 'navigation-less'

  @Prop({
    required: true,
    default: true,
  })
  readonly isShadow!: boolean

  @Prop({
    required: false,
    default: 'left',
  })
  readonly logoPosition!: 'left' | 'center'

  @Prop({
    type: String,
    required: false,
    default: 'light',
  })
  readonly theme!: Theme

  @Prop({
    required: false,
    default: false,
  })
  readonly showMobileSwitcher!: boolean

  private isMobileNavActive = false
  private isShadowAppliedByScroll = false

  // START TODO: move this to layout
  private isShowMobileFilterButton = false
  private currentScrollTop = 0
  private startScrollAppearanceOnMobile = 130
  private startScrollAppearanceOnDesktop = 430
  private availableRouteList = [this.localePath('compare-bundle'), this.externalCompareHomeHref, this.externalCompareMobileHref]

  get externalIndexHref (): string {
    return getExternalIndexHref(this.$config.BASE_URL, this.localePath)
  }

  get externalCompareMobileHref (): string {
    return getExternalCompareMobileHref(this.$config.BASE_URL, this.localePath)
  }

  get externalCompareHomeHref (): string {
    return getExternalCompareHomeHref(this.$config.BASE_URL, this.localePath)
  }

  get logoImage () {
    return this.theme === 'light'
      ? require('~/assets/images/alao-logo.svg')
      : require('~/assets/images/icons/alao-logo-white-orange.svg')
  }

  get aboutMenuItems (): DropdownMenuItem[] {
    return [
      {
        title: this.$t('header.about.ourStory') as string,
        link: this.localePath('about-us'),
        dataE2e: 'headerOurStoryLink',
      },
      {
        title: this.$t('header.about.howItWorks') as string,
        link: this.localePath('how-it-works'),
        dataE2e: 'headerHowItWorksLink',
      },
      {
        title: this.$t('header.about.coverage') as string,
        link: this.localePath('coverage'),
        dataE2e: 'headerCoverageLink',
      },
      {
        title: this.$t('charity.project') as string,
        link: this.localePath('charity'),
        dataE2e: 'headerCharityLink',
      },
    ]
  }

  private saveCurrentScrollPage () {
    if (this.activatePanel) {
      this.currentScrollTop = window.pageYOffset
    }
  }

  get activatePanel (): boolean {
    return this.$mqUtils.isMobile || this.$mqUtils.isMD || this.$mqUtils.isLG
  }

  get startPosition (): number {
    if (this.$mqUtils.isMobile) {
      return this.startScrollAppearanceOnMobile
    } else {
      return this.startScrollAppearanceOnDesktop
    }
  }

  get googleRatingSize () {
    return this.$mqUtils.isDesktop && !this.$mqUtils.isXL && !this.$mqUtils.isMD ? 'md' : 'sm'
  }

  @Watch('currentScrollTop')
  onChangeCurrentScrollTop (currentValue: number, prevValue: number) {
    this.isShowMobileFilterButton = currentValue < prevValue
  }

  get showB2B () {
    return this.$featureFlags.isEnabled(FeatureFlags.B2B_PAGE) && this.$i18n.locale === 'de'
  }

  get showGoogleRating (): boolean {
    const availableRouteList = new Set([
      this.localeRoute({
        name: 'compare-bundle',
      })?.name,
      this.localeRoute({
        name: 'compare-home',
      })?.name,
      this.localeRoute({
        name: 'compare-mobile',
      })?.name,
      this.localeRoute({
        name: 'compare-bundle-slug',
      })?.name,
      this.localeRoute({
        name: 'compare-home-slug',
      })?.name,
      this.localeRoute({
        name: 'compare-mobile-slug',
      })?.name,
      this.localeRoute({
        name: 'order-slug',
      })?.name,
    ])
    return availableRouteList.has(this.$route.name) && (!this.$mqUtils.isLG)
  }

  toggleMobileNav () {
    this.isMobileNavActive = !this.isMobileNavActive
  }

  onScrollHandler () {
    this.isShadowAppliedByScroll = window.scrollY > 50
  }

  navigateTo (localizedPath: string) {
    this.$router.push(localizedPath)
  }

  mounted () {
    window.addEventListener('scroll', this.onScrollHandler.bind(this), { passive: true })
  }

  beforeDestroy () {
    window.removeEventListener('scroll', this.onScrollHandler.bind(this))

    // TODO: move this to layout
    document.removeEventListener('scroll', this.saveCurrentScrollPage)
  }

  // TODO: move this to layout
  @Watch('$route.path', { immediate: true })
  handlerRouteChange (currentRoute: string, prevRoute: string) {
    if (process.client) {
      if (this.availableRouteList.includes(prevRoute)) {
        document.removeEventListener('scroll', this.saveCurrentScrollPage, true)
      }

      if (this.availableRouteList.includes(currentRoute)) {
        document.addEventListener('scroll', this.saveCurrentScrollPage, true)
      }
    }
  }
}
